import { useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Check, X, Phone, Mail, Users2, IndianRupee } from 'lucide-react';
import { useAdminPlanRequests, useApprovePlanRequest, useRejectPlanRequest } from '@/api/queries/superAdmin.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// Every Custom customer can get genuinely different commercial terms — set
// right at approval time. Leaving a field blank falls back to the shared
// CUSTOM plan defaults (editable later from Plan Management or this same
// company's row, once it appears in a future "Custom Companies" list).
function CustomTermsModal({ request, onClose, onConfirm, submitting }) {
  const [form, setForm] = useState({ price_per_employee: '', employee_limit: '', lens_device_limit: '', company_limit: '' });
  const inputCls = 'w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]';
  const numOrUndef = (v) => (v === '' ? undefined : Number(v));

  const handleConfirm = () => {
    const custom_plan_config = {
      price_per_employee: numOrUndef(form.price_per_employee),
      employee_limit: numOrUndef(form.employee_limit),
      lens_device_limit: numOrUndef(form.lens_device_limit),
      company_limit: numOrUndef(form.company_limit),
    };
    const hasAny = Object.values(custom_plan_config).some(v => v !== undefined);
    onConfirm(hasAny ? custom_plan_config : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] w-full max-w-sm border border-[var(--border)] shadow-[var(--shadow-xl)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <div>
            <p className="text-[11px] font-semibold uppercase text-[var(--text-tertiary)]">Approving</p>
            <h2 className="text-[16px] font-bold text-[var(--text-primary)]">{request.contact_company_name}</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-[12px] text-[var(--text-secondary)]">Set this customer's own terms (leave blank to use the shared Custom plan defaults — editable any time from Plan Management).</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">₹/employee/mo</label>
              <input className={inputCls} type="number" placeholder="Default" value={form.price_per_employee} onChange={e => setForm(f => ({ ...f, price_per_employee: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Employee limit</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.employee_limit} onChange={e => setForm(f => ({ ...f, employee_limit: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">Lens device limit</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.lens_device_limit} onChange={e => setForm(f => ({ ...f, lens_device_limit: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-semibold text-[var(--text-secondary)]">No. of companies</label>
              <input className={inputCls} type="number" placeholder="Unlimited" value={form.company_limit} onChange={e => setForm(f => ({ ...f, company_limit: e.target.value }))} />
            </div>
          </div>
          <Button onClick={handleConfirm} disabled={submitting} className="w-full">
            <Check size={14}/> {submitting ? 'Approving…' : 'Approve & Allow Multi-Company'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RequestCard({ r, onApproveClick, onReject, approving, rejecting, approveLabel }) {
  return (
    <div className="card flex flex-col gap-3 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[14px] font-bold text-[var(--text-primary)]">{r.contact_company_name || '—'}</p>
          <p className="text-[12px] text-[var(--text-secondary)]">{r.contact_name}</p>
        </div>
        <Badge variant={r.status === 'PENDING' ? 'yellow' : r.status === 'APPROVED' ? 'green' : 'red'}>{r.status}</Badge>
      </div>
      <div className="space-y-1 text-[12px] text-[var(--text-secondary)]">
        {r.contact_phone && <p className="flex items-center gap-1.5"><Phone size={12}/> {r.contact_phone}</p>}
        {r.contact_email && <p className="flex items-center gap-1.5"><Mail size={12}/> {r.contact_email}</p>}
        {r.employee_range && <p className="flex items-center gap-1.5"><Users2 size={12}/> {r.employee_range} employees</p>}
      </div>
      {r.message && <p className="text-[12px] text-[var(--text-tertiary)] bg-[var(--bg-muted)] rounded-[8px] p-2.5">{r.message}</p>}
      <p className="text-[11px] text-[var(--text-tertiary)]">{r.created_at ? format(new Date(r.created_at), 'd MMM yyyy, h:mm a') : '—'}</p>
      {r.status === 'PENDING' && (
        <div className="flex gap-2 pt-1">
          <Button size="xs" variant="success" disabled={approving} onClick={() => onApproveClick(r)} className="flex-1">
            <Check size={12}/> {approveLabel}
          </Button>
          <Button size="xs" variant="danger" disabled={rejecting} onClick={() => onReject(r.id)} className="flex-1">
            <X size={12}/> Reject
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SuperAdminCustomRequestsPage() {
  const { data: registeredResp, isLoading: loadingRegistered } = useAdminPlanRequests({ type: 'CUSTOM_REGISTERED' });
  const { data: leadResp, isLoading: loadingLeads } = useAdminPlanRequests({ type: 'CUSTOM_LEAD' });
  const { mutate: approve, isPending: approving } = useApprovePlanRequest();
  const { mutate: reject, isPending: rejecting } = useRejectPlanRequest();
  const [termsFor, setTermsFor] = useState(null); // the registered request currently being approved with custom terms

  const registered = registeredResp?.data ?? [];
  const leads = leadResp?.data ?? [];

  // Leads have no company yet — approve directly, no terms to set.
  const handleApproveLead = (r) => approve(r.id, {
    onSuccess: () => toast.success('Lead marked approved — invite them to sign up'),
    onError: () => toast.error('Failed to approve request'),
  });

  const handleConfirmTerms = (custom_plan_config) => {
    approve({ id: termsFor.id, custom_plan_config }, {
      onSuccess: () => { toast.success('Custom plan approved'); setTermsFor(null); },
      onError: () => toast.error('Failed to approve request'),
    });
  };

  const handleReject = (id) => reject({ id, reject_reason: 'Not eligible' }, {
    onSuccess: () => toast.success('Request rejected'),
    onError: () => toast.error('Failed to reject request'),
  });

  return (
    <div className="space-y-6 fade-in max-w-5xl">
      <PageHeader
        title="Custom Plan Requests"
        subtitle="Registered customers skip the form — we already have their details. Leads need a callback."
        breadcrumb={[{ label: 'Super Admin', href: '/super-admin' }, { label: 'Custom Requests' }]}
      />

      <div>
        <p className="text-[13px] font-bold text-[var(--text-primary)] mb-3">Registered customers — direct request</p>
        {loadingRegistered ? (
          <p className="text-[13px] text-[var(--text-secondary)]">Loading…</p>
        ) : registered.length === 0 ? (
          <p className="text-[13px] text-[var(--text-secondary)] card text-center py-10">No registered customers requesting Custom right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {registered.map(r => (
              <RequestCard key={r.id} r={r} onApproveClick={setTermsFor} onReject={handleReject}
                approving={approving} rejecting={rejecting} approveLabel="Approve & Set Terms" />
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-[13px] font-bold text-[var(--text-primary)] mb-3">Unregistered leads — needs callback</p>
        {loadingLeads ? (
          <p className="text-[13px] text-[var(--text-secondary)]">Loading…</p>
        ) : leads.length === 0 ? (
          <p className="text-[13px] text-[var(--text-secondary)] card text-center py-10">No website leads yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {leads.map(r => (
              <RequestCard key={r.id} r={r} onApproveClick={handleApproveLead} onReject={handleReject}
                approving={approving} rejecting={rejecting} approveLabel="Mark approved — invite manually" />
            ))}
          </div>
        )}
      </div>

      {termsFor && (
        <CustomTermsModal request={termsFor} submitting={approving}
          onClose={() => setTermsFor(null)} onConfirm={handleConfirmTerms} />
      )}
    </div>
  );
}
